import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plan } from '../../types/plan';
import { BillingInterval, BILLING_INTERVAL_LABELS } from '../../types/licensingEnums';
import { api } from '../../services/api';
import { featureService } from '../../services/featureService';
import { planFeatureService } from '../../services/planFeatureService';
import type { Feature } from '../../types/feature';
import { useToast } from '../../components/UI/ToastProvider';
import { PLAN_PRICE_MAX, validatePlanPrice } from '../../utils/planPrice';

const initialForm: Omit<Plan, 'id' | 'createdAt' | 'moduleCodes'> = {
  code: '',
  name: '',
  description: '',
  price: 0,
  active: true,
  billingInterval: BillingInterval.Monthly,
  sortOrder: 0,
};

type TabId = 'dados' | 'features';

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;

  if (typeof data === 'string' && data.trim())
  {
    return data;
  }

  const response = data as { message?: string; error?: string; title?: string; detail?: string } | undefined;
  if (response?.detail)
  {
    return response.detail;
  }
  if (response?.error)
  {
    return response.error;
  }
  if (response?.message)
  {
    return response.message;
  }
  if (response?.title && response.title !== 'An error occurred while processing your request.')
  {
    return response.title;
  }
  return fallback;
};

export const PlanEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<typeof initialForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [activeTab, setActiveTab] = useState<TabId>('dados');
  const [allFeatures, setAllFeatures] = useState<Feature[]>([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(new Set());
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const moduleCheckboxRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const { addToast } = useToast();
  const navigate = useNavigate();
  const isNew = !id;

  useEffect(() => {
    if (!isNew)
    {
      void fetchPlan();
    }
    else
    {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'features')
    {
      void loadFeaturesTab();
    }
  }, [activeTab, id]);

  const fetchPlan = async () => {
    setLoading(true);
    try
    {
      const response = await api.get(`/plans/${id}`);
      setForm({
        code: response.data.code,
        name: response.data.name,
        description: response.data.description || '',
        price: response.data.price,
        active: response.data.active,
        billingInterval: response.data.billingInterval ?? BillingInterval.Monthly,
        sortOrder: response.data.sortOrder ?? 0,
      });
    }
    catch
    {
      addToast('Erro ao carregar dados do plano.', 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  const loadFeaturesTab = async () => {
    setFeaturesLoading(true);
    try
    {
      const features = await featureService.getAll();
      setAllFeatures(features.filter((f) => f.active));

      if (id)
      {
        const planFeatures = await planFeatureService.getByPlanId(id);
        setSelectedFeatureIds(new Set(planFeatures.map((pf) => pf.featureId)));
      }
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao carregar features do plano.'), 'error');
    }
    finally
    {
      setFeaturesLoading(false);
    }
  };

  const featuresByModule = useMemo(() => {
    const grouped = new Map<string, Feature[]>();
    for (const feature of allFeatures)
    {
      const moduleCode = feature.productModuleCode ?? 'OUTROS';
      const existing = grouped.get(moduleCode) ?? [];
      existing.push(feature);
      grouped.set(moduleCode, existing);
    }
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [allFeatures]);

  const selectedFeatureCodes = useMemo(() => {
    return allFeatures
      .filter((f) => selectedFeatureIds.has(f.id))
      .map((f) => f.code)
      .sort();
  }, [allFeatures, selectedFeatureIds]);

  const selectedModuleCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const feature of allFeatures)
    {
      if (selectedFeatureIds.has(feature.id) && feature.productModuleCode)
      {
        codes.add(feature.productModuleCode);
      }
    }
    return Array.from(codes).sort();
  }, [allFeatures, selectedFeatureIds]);

  useEffect(() => {
    for (const [moduleCode, moduleFeatures] of featuresByModule)
    {
      const checkbox = moduleCheckboxRefs.current[moduleCode];
      if (!checkbox)
      {
        continue;
      }

      const selectedCount = moduleFeatures.filter((f) => selectedFeatureIds.has(f.id)).length;
      checkbox.indeterminate = selectedCount > 0 && selectedCount < moduleFeatures.length;
    }
  }, [featuresByModule, selectedFeatureIds]);

  const isModuleFullySelected = (moduleFeatures: Feature[]) => {
    return moduleFeatures.length > 0 && moduleFeatures.every((f) => selectedFeatureIds.has(f.id));
  };

  const toggleModule = (moduleFeatures: Feature[], includeModule: boolean) => {
    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      for (const feature of moduleFeatures)
      {
        if (includeModule)
        {
          next.add(feature.id);
        }
        else
        {
          next.delete(feature.id);
        }
      }
      return next;
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | number | boolean = value;

    if (type === 'number')
    {
      newValue = value === '' ? 0 : parseFloat(value);
    }
    else if (type === 'checkbox')
    {
      newValue = (e.target as HTMLInputElement).checked;
    }
    else if (name === 'billingInterval' || name === 'sortOrder')
    {
      newValue = parseInt(value, 10);
    }

    setForm({ ...form, [name]: newValue });
  };

  const toggleFeature = (featureId: string) => {
    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(featureId))
      {
        next.delete(featureId);
      }
      else
      {
        next.add(featureId);
      }
      return next;
    });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.code.trim())
    {
      newErrors.code = 'Código é obrigatório';
    }
    if (!form.name.trim())
    {
      newErrors.name = 'Nome é obrigatório';
    }

    const priceError = validatePlanPrice(form.price);
    if (priceError)
    {
      newErrors.price = priceError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const savePlan = async () => {
    if (!validateForm())
    {
      setActiveTab('dados');
      return;
    }

    setSaving(true);
    try
    {
      if (isNew)
      {
        const payload = { ...form, featureIds: Array.from(selectedFeatureIds) };
        await api.post('/plans', payload);
        addToast('Plano criado com sucesso!', 'success');
      }
      else
      {
        await api.put(`/plans/${id}`, form);
        addToast('Plano atualizado com sucesso!', 'success');
      }
      navigate('/admin/plans');
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, isNew ? 'Erro ao criar plano.' : 'Erro ao atualizar plano.'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await savePlan();
  };

  const handleSaveFeatures = async () => {
    if (!id)
    {
      return;
    }

    setSavingFeatures(true);
    try
    {
      await planFeatureService.setPlanFeatures(id, {
        featureIds: Array.from(selectedFeatureIds),
      });
      addToast('Features do plano salvas com sucesso!', 'success');
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao salvar features do plano.'), 'error');
    }
    finally
    {
      setSavingFeatures(false);
    }
  };

  if (loading)
  {
    return <div className="p-8">Carregando...</div>;
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">{isNew ? 'Novo Plano' : 'Editar Plano'}</h1>

      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            type="button"
            onClick={() => setActiveTab('dados')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'dados'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Dados do plano
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('features')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'features'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Aplicações e features
          </button>
        </nav>
      </div>

      {activeTab === 'dados' && (
        <form onSubmit={handleSubmit} className="max-w-2xl bg-white rounded shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1">Código *</label>
              <input
                type="text"
                name="code"
                value={form.code}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                maxLength={50}
                required
              />
              {errors.code && <span className="text-red-600 text-sm">{errors.code}</span>}
            </div>

            <div>
              <label className="block font-medium mb-1">Nome *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                maxLength={100}
                required
              />
              {errors.name && <span className="text-red-600 text-sm">{errors.name}</span>}
            </div>

            <div className="md:col-span-2">
              <label className="block font-medium mb-1">Descrição</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                rows={3}
                maxLength={200}
              />
            </div>

            <div>
              <label className="block font-medium mb-1">Preço *</label>
              <input
                type="number"
                name="price"
                value={form.price}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                min="0"
                max={PLAN_PRICE_MAX}
                step="0.01"
                required
              />
              {errors.price && <span className="text-red-600 text-sm">{errors.price}</span>}
            </div>

            <div>
              <label className="block font-medium mb-1">Intervalo de cobrança</label>
              <select
                name="billingInterval"
                value={form.billingInterval}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              >
                {Object.entries(BILLING_INTERVAL_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-medium mb-1">Ordem de exibição</label>
              <input
                type="number"
                name="sortOrder"
                value={form.sortOrder}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                min="0"
              />
            </div>

            <div className="flex items-center mt-6">
              <label className="block font-medium mr-2">Ativo</label>
              <input
                type="checkbox"
                name="active"
                checked={form.active}
                onChange={handleInputChange}
                className="h-5 w-5 cursor-pointer"
              />
            </div>
          </div>

          <p className="mt-4 text-sm text-gray-600">
            {isNew
              ? 'As aplicações marcadas na aba '
              : 'As aplicações incluídas neste plano são definidas na aba '}
            <button
              type="button"
              className="text-blue-600 hover:underline font-medium"
              onClick={() => setActiveTab('features')}
            >
              Aplicações e features
            </button>
            {isNew ? ' serão vinculadas ao salvar o plano.' : '.'}
          </p>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              onClick={() => navigate('/admin/plans')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'features' && (
        <div className="max-w-3xl bg-white rounded shadow p-6">
          {featuresLoading ? (
            <div className="py-8 text-center text-gray-500">Carregando features...</div>
          ) : (
            <>
              <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-1">Aplicações incluídas</p>
                <p className="text-xs text-gray-500 mb-3">
                  Marque as aplicações desejadas para incluir ou remover todas as features de cada produto.
                  A combinação exata de funcionalidades é ajustada abaixo.
                </p>
                {featuresByModule.length > 0 ? (
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    {featuresByModule.map(([moduleCode, moduleFeatures]) => (
                      <label
                        key={moduleCode}
                        className="flex items-center gap-2 cursor-pointer select-none"
                      >
                        <input
                          ref={(el) => {
                            moduleCheckboxRefs.current[moduleCode] = el;
                          }}
                          type="checkbox"
                          checked={isModuleFullySelected(moduleFeatures)}
                          onChange={(e) => toggleModule(moduleFeatures, e.target.checked)}
                          className="h-4 w-4"
                        />
                        <span className="text-sm font-semibold uppercase tracking-wide text-gray-800">
                          {moduleCode}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({moduleFeatures.filter((f) => selectedFeatureIds.has(f.id)).length}/{moduleFeatures.length})
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Nenhuma feature ativa cadastrada. Cadastre features no catálogo primeiro.
                  </p>
                )}
                {selectedModuleCodes.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedModuleCodes.map((code) => (
                      <span
                        key={code}
                        className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold uppercase text-purple-800"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {selectedFeatureCodes.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Features selecionadas ({selectedFeatureCodes.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedFeatureCodes.map((code) => (
                      <span
                        key={code}
                        className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-mono font-medium text-blue-800"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {featuresByModule.map(([moduleCode, moduleFeatures]) => (
                  <div key={moduleCode}>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                      {moduleCode}
                    </h3>
                    <div className="space-y-2 border border-gray-100 rounded-md p-3">
                      {moduleFeatures.map((feature) => (
                        <label
                          key={feature.id}
                          className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 rounded p-1"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFeatureIds.has(feature.id)}
                            onChange={() => toggleFeature(feature.id)}
                            className="mt-1 h-4 w-4"
                          />
                          <div>
                            <span className="text-sm font-mono text-gray-900">{feature.code}</span>
                            <span className="text-sm text-gray-600 ml-2">{feature.name}</span>
                            {feature.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  onClick={() => navigate('/admin/plans')}
                >
                  {isNew ? 'Cancelar' : 'Voltar'}
                </button>
                {isNew ? (
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                    disabled={saving}
                    onClick={() => void savePlan()}
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                    disabled={savingFeatures}
                    onClick={() => void handleSaveFeatures()}
                  >
                    {savingFeatures ? 'Salvando...' : 'Salvar features'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
